import DialogContent from '@mui/material/DialogContent'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { layoutAPI } from 'api/LayoutAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import { useNotificationPopup } from 'Components/reusable/Notification'
import React, { useEffect, useState } from 'react'
import { IError } from 'types/error'
import { IItemOrder, IItemVisibility, ILayout, INewLayout } from 'types/layoutInterfaces'
import { usePreviewData } from './PrintPreviewProvider'

/** @notExported */
interface ILayoutModalProps {
  /** Layout data */
  data: INewLayout
  /** Submit indicator */
  submitIndicator: boolean
  /** Function to reset submit */
  resetSubmit: () => void
  /** CV ID */
  cvId: number
  /** Function to close modal */
  onClose: (newLayout?: ILayout) => void
  /** Item orders */
  orders: IItemOrder[]
  /** Item visibilities */
  visibilities: IItemVisibility[]
}

/**
 * Layout modal component.
 *
 * @returns Layout modal component.
 * @notExported
 */
const LayoutModal: React.FC<ILayoutModalProps> = ({
  data,
  submitIndicator,
  resetSubmit,
  cvId,
  onClose,
  orders = [],
  visibilities = [],
}) => {
  const [backendError, setBackendError] = useState<IError>()
  const { setSuccessNotificationPopup } = useNotificationPopup()
  const { setVisibleLayout } = usePreviewData()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (data && submitIndicator) {
        try {
          const newLayout = await layoutAPI.createLayout(cvId, orders, visibilities, data?.name, controller)
          setVisibleLayout(newLayout)
          setSuccessNotificationPopup()
          onClose(newLayout)
        } catch (err) {
          setBackendError(err as IError)
          resetSubmit()
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [
    submitIndicator,
    cvId,
    orders,
    visibilities,
    data,
    onClose,
    resetSubmit,
    setSuccessNotificationPopup,
    setVisibleLayout,
  ])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent>
      <DataEditContextTextField<INewLayout> field="name" required fullWidth insetLabel />
    </DialogContent>
  )
}

export default newWithEditModal(LayoutModal)
